import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import { Message } from 'components/Message';
import { ChatMessage, ChatSession } from 'types/types';
import { useAppState } from 'hooks/GlobalContext';
import CopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';

interface Props {
  open: boolean;
  onClose: () => void;
  session: ChatSession | null;
}

const AdminSessionModal = ({ open, onClose, session }: Props): React.ReactElement | null => {
  const { apiClient } = useAuth();
  const { setSnack, setSelectedCandidate } = useAppState();
  const [loading, setLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !session?.id) return;

    const sessionId = session.id!;

    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const resp = await apiClient.getChatMessages(sessionId, { limit: 200 });
        if (!mounted) return;
        setMessages(resp.data || []);
      } catch (error) {
        console.error('Failed to load session messages', error);
        setSnack('Failed to load session messages', 'error');
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [open, session, apiClient, setSnack]);

  if (!session) return null;

  const handleCopy = async (): Promise<void> => {
    if (!session?.id) {
      setSnack('No session id available', 'error');
      return;
    }

    try {
      await navigator.clipboard.writeText(session.id);
      setSnack('Session ID copied to clipboard', 'success');
    } catch (error) {
      console.error('Failed to copy session id', error);
      setSnack('Failed to copy session id', 'error');
    }
  };

  const handleOpenInChat = async (): Promise<void> => {
    // If session has a userId, try to load that candidate and navigate to chat
    const userRef = session.userId || session.guestId || null;
    if (!userRef) {
      setSnack('No user associated with this session', 'error');
      return;
    }

    try {
      const candidate = await apiClient.getCandidate(userRef);
      if (candidate) {
        setSelectedCandidate(candidate);
        navigate('/chat');
        onClose();
      } else {
        setSnack('Unable to find candidate for this session', 'error');
      }
    } catch (error) {
      console.error('Failed to open in chat', error);
      setSnack('Failed to open in chat', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Session Details
        <Box component="span" sx={{ ml: 2, fontSize: '0.85rem' }}>
          {session.id}
        </Box>
        <IconButton onClick={handleCopy} size="small" sx={{ ml: 1 }} aria-label="copy-session-id">
          <CopyIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2">User</Typography>
          <Typography variant="body2">{session.userId || session.guestId || 'Unknown'}</Typography>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Title
          </Typography>
          <Typography variant="body2">{session.title || ''}</Typography>
        </Box>

        <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && messages.length === 0 && (
            <Typography variant="body2">No messages in this session.</Typography>
          )}

          {!loading &&
            messages.map(msg => {
              return (
                <Box key={msg.id} sx={{ mb: 1 }}>
                  <Message message={msg} chatSession={session} />
                </Box>
              );
            })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleOpenInChat} variant="contained">
          Open in Chat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AdminSessionModal };
